import { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import baggageStatsService from "services/admin/baggageStatsService";
import { baggageStatusLabel } from "pages/Admin/Baggages/Baggage/baggage-status";
import ChartCanvas from "./ChartCanvas";
import RouteAnalytics from "./RouteAnalytics";
import FillAnalytics from "./FillAnalytics";

const PALETTE = [
  "#3880ff",
  "#2dd36f",
  "#ffc409",
  "#eb445a",
  "#6a64ff",
  "#0cd1e8",
  "#f4a261",
  "#92949c",
];

const CLIENT_TYPE_LABELS: Record<string, string> = {
  individual: "Физ. лицо",
  legal: "Юр. лицо",
};
const PAYMENT_LABELS: Record<string, string> = {
  cash: "Наличные",
  kaspi: "Kaspi QR",
  card: "Карта",
  transfer: "Перевод",
  "не указан": "Не указан",
};

const fmt = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return fmt(d);
};

const BaggageStats = () => {
  const orgID = localStorage.getItem("orgID") || "";
  const [tab, setTab] = useState<"overview" | "routes" | "fill">("overview");
  const [preset, setPreset] = useState<"7" | "30" | "90" | "custom">("30");
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(fmt(new Date()));

  const applyPreset = (p: "7" | "30" | "90" | "custom") => {
    setPreset(p);
    if (p !== "custom") {
      setFrom(daysAgo(Number(p)));
      setTo(fmt(new Date()));
    }
  };

  const { data: stats, isLoading } = useQuery({
    enabled: tab === "overview",
    queryKey: ["baggage-stats", { orgID, from, to }],
    queryFn: async () => (await baggageStatsService.getStats({ from, to })).data,
  });

  const currency = stats?.currency || "KZT";
  const money = (n: number) => `${Number(n || 0).toLocaleString("ru-RU")} ${currency}`;

  const kpiCards = useMemo(() => {
    const k = stats?.kpi;
    if (!k) return [];
    return [
      { label: "Отправок", value: k.tripsCount },
      { label: "Мест багажа", value: k.piecesCount },
      { label: "Вес, кг", value: Number(k.totalWeight).toLocaleString("ru-RU") },
      { label: "Выручка (оплачено)", value: money(k.revenuePaid), accent: "#2dd36f" },
      { label: "В работе", value: k.inProgressPieces, accent: "#3880ff" },
      { label: "Выдано", value: k.deliveredPieces },
      {
        label: "Проблемных",
        value: k.problemPieces,
        accent: k.problemPieces ? "#eb445a" : undefined,
      },
      {
        label: "Не оплачено",
        value: `${k.unpaidCount} · ${money(k.unpaidAmount)}`,
        accent: k.unpaidCount ? "#ffc409" : undefined,
      },
      { label: "Средний чек", value: money(k.avgCheck) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  // ---- данные графиков ----
  const dynamicsData = useMemo(() => {
    const d = stats?.dynamics || [];
    return {
      labels: d.map((x: any) => x.date),
      datasets: [
        {
          type: "bar" as const,
          label: "Отправки",
          data: d.map((x: any) => x.trips),
          backgroundColor: "rgba(56,128,255,0.55)",
          yAxisID: "y",
        },
        {
          type: "line" as const,
          label: `Выручка, ${currency}`,
          data: d.map((x: any) => x.revenue),
          borderColor: "#2dd36f",
          backgroundColor: "#2dd36f",
          tension: 0.3,
          yAxisID: "y1",
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  const dynamicsOptions = useMemo(
    () => ({
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        y: {
          position: "left" as const,
          beginAtZero: true,
          title: { display: true, text: "Отправки" },
        },
        y1: {
          position: "right" as const,
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          title: { display: true, text: "Выручка" },
        },
      },
    }),
    [],
  );

  const statusData = useMemo(() => {
    const s = stats?.statusDistribution || [];
    return {
      labels: s.map((x: any) => baggageStatusLabel(x.status)),
      datasets: [{ data: s.map((x: any) => x.count), backgroundColor: PALETTE, borderWidth: 0 }],
    };
  }, [stats]);

  const financeStructureData = useMemo(() => {
    const f = stats?.finance;
    return {
      labels: ["Перевозка", "Сборы", "Скидка"],
      datasets: [
        {
          label: currency,
          data: [f?.carriageTotal || 0, f?.feesTotal || 0, f?.discountTotal || 0],
          backgroundColor: ["#3880ff", "#0cd1e8", "#eb445a"],
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  const paymentData = useMemo(() => {
    const m = stats?.finance?.byPaymentMethod || [];
    return {
      labels: m.map((x: any) => PAYMENT_LABELS[x.method] || x.method),
      datasets: [{ data: m.map((x: any) => x.amount), backgroundColor: PALETTE, borderWidth: 0 }],
    };
  }, [stats]);

  const clientTypeData = useMemo(() => {
    const c = stats?.finance?.byClientType || [];
    return {
      labels: c.map((x: any) => CLIENT_TYPE_LABELS[x.clientType] || x.clientType),
      datasets: [{ data: c.map((x: any) => x.count), backgroundColor: PALETTE, borderWidth: 0 }],
    };
  }, [stats]);

  const hasData = (stats?.kpi?.tripsCount ?? 0) > 0;

  return (
    <div>
      <div
        className="m-b-16"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h5 style={{ margin: 0 }}>Статистика багажа (КТЖ)</h5>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_e, v) => v && applyPreset(v)}
          >
            <ToggleButton value="7">7 дней</ToggleButton>
            <ToggleButton value="30">30 дней</ToggleButton>
            <ToggleButton value="90">90 дней</ToggleButton>
            <ToggleButton value="custom">Свой</ToggleButton>
          </ToggleButtonGroup>
          {preset === "custom" && (
            <>
              <TextField
                size="small"
                type="date"
                label="С"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <TextField
                size="small"
                type="date"
                label="По"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="overview" label="Обзор" />
        <Tab value="routes" label="Маршруты" />
        <Tab value="fill" label="Заполненность" />
      </Tabs>

      {tab === "routes" ? (
        <RouteAnalytics from={from} to={to} />
      ) : tab === "fill" ? (
        <FillAnalytics from={from} to={to} />
      ) : isLoading ? (
        <div className="text-center m-t-32">
          <CircularProgress />
        </div>
      ) : !hasData ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              За выбранный период нет данных. Измените диапазон.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 2,
              mb: 3,
            }}
          >
            {kpiCards.map((c: any) => (
              <Card key={c.label} variant="outlined">
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {c.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: c.accent || "inherit" }}>
                    {c.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Динамика на всю ширину */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Динамика: отправки и выручка по дням
              </Typography>
              <ChartCanvas type="bar" data={dynamicsData} options={dynamicsOptions} height={300} />
            </CardContent>
          </Card>

          {/* Сетка графиков */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 3,
            }}
          >
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Места по статусам
                </Typography>
                <ChartCanvas type="doughnut" data={statusData} />
              </CardContent>
            </Card>

            {/* <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Структура стоимости
                </Typography>
                <ChartCanvas
                  type="bar"
                  data={financeStructureData}
                  options={{ plugins: { legend: { display: false } } }}
                />
              </CardContent>
            </Card> */}

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Способы оплаты (оплачено)
                </Typography>
                <ChartCanvas type="doughnut" data={paymentData} />
              </CardContent>
            </Card>

            {/* <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Тип клиента
                </Typography>
                <ChartCanvas type="doughnut" data={clientTypeData} />
              </CardContent>
            </Card> */}
          </Box>

          {/* Финансовая сводка */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 2,
              mt: 3,
            }}
          >
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Оплачено
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#2dd36f" }}>
                  {money(stats.finance.revenuePaid)}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Не оплачено
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffc409" }}>
                  {money(stats.finance.revenueUnpaid)}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Возвраты
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#eb445a" }}>
                  {money(stats.finance.revenueRefunded)}
                </Typography>
              </CardContent>
            </Card>
            {/* <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Скидки по билетам
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {money(stats.finance.discountTotal)}
                </Typography>
              </CardContent>
            </Card> */}
          </Box>
        </>
      )}
    </div>
  );
};

export default BaggageStats;
